import * as XLSX from "xlsx";
import { getFilteredStats } from "./helpers";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const sumField = (arr, key) =>
  (arr ?? []).reduce((acc, r) => acc + (Number(r[key]) || 0), 0);

const fmt = (v) => Number(Number(v).toFixed(2));

// Build a worksheet from a 2-D array of values
const makeSheet = (rows) => XLSX.utils.aoa_to_sheet(rows);

// Append a worksheet to a workbook
const addSheet = (wb, ws, name) => {
  XLSX.utils.book_append_sheet(wb, ws, name);
};

// ─────────────────────────────────────────────────────────────────────────────
// Sheet builders
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Sheet 1 — Overview
 * Summary totals for every top-level metric.
 * Single mode  : Metric | Value
 * Compare mode : Metric | Scheme A | Scheme B | Winner
 */
const buildOverviewSheet = (sA, sB, nameA, nameB, winner) => {
  const isSingle = !sB;

  const metrics = [
    {
      label: "Total Participants",
      a: sumField(sA?.states, "total_participants"),
      b: sB ? sumField(sB?.states, "total_participants") : null,
    },
    {
      label: "Points Earned",
      a: fmt(sumField(sA?.states, "total_points_earned")),
      b: sB ? fmt(sumField(sB?.states, "total_points_earned")) : null,
    },
    {
      label: "Points Redeemed",
      a: fmt(sumField(sA?.states, "total_points_redeemed")),
      b: sB ? fmt(sumField(sB?.states, "total_points_redeemed")) : null,
    },
    {
      label: "Total Product Scans",
      a: sumField(sA?.product_codes, "total_scans"),
      b: sB ? sumField(sB?.product_codes, "total_scans") : null,
    },
    {
      label: "Unique Users",
      a: sumField(sA?.product_codes, "unique_users"),
      b: sB ? sumField(sB?.product_codes, "unique_users") : null,
    },
    {
      label: "Category Scans",
      a: sumField(sA?.product_categories, "total_scans"),
      b: sB ? sumField(sB?.product_categories, "total_scans") : null,
    },
  ];

  const rows = [];

  // Title row
  rows.push([
    isSingle ? `${nameA} — Stats Overview` : `${nameA}  vs  ${nameB}`,
  ]);
  rows.push([]); // blank spacer

  // Header row
  if (isSingle) {
    rows.push(["Metric", "Value"]);
  } else {
    rows.push(["Metric", nameA, nameB, "Winner"]);
  }

  // Data rows
  metrics.forEach(({ label, a, b }) => {
    if (isSingle) {
      rows.push([label, a]);
    } else {
      let winnerLabel = "Tie";
      if (a > b) winnerLabel = nameA;
      else if (b > a) winnerLabel = nameB;
      rows.push([label, a, b, winnerLabel]);
    }
  });

  // Blank spacer + winner summary for compare mode
  if (!isSingle && winner) {
    rows.push([]);
    if (winner.overall === "tie") {
      rows.push(["Result", "It's a Draw!"]);
    } else {
      const wName = winner.overall === "A" ? nameA : nameB;
      rows.push(["Winner", wName]);
      rows.push(["Score", `${winner.scoreA} - ${winner.scoreB}`]);
      rows.push(["Reason", winner.reason]);
    }
  }

  return makeSheet(rows);
};

/**
 * Sheets 2–5 — Detail tables (State / Product / Category / Points Range)
 *
 * Single mode  : key | col1 | col2 ...
 * Compare mode : key | A_col1 | B_col1 | A_col2 | B_col2 ... | Winner
 *
 * Ends with a TOTAL row using Excel SUM formulas.
 */
const buildDetailSheet = ({
  rowsA,
  rowsB,
  keyField,
  keyLabel,
  valueCols, // [{ field, label }]
  nameA,
  nameB,
}) => {
  const isSingle = !rowsB;
  const mapA = Object.fromEntries((rowsA ?? []).map((r) => [r[keyField], r]));
  const mapB = Object.fromEntries((rowsB ?? []).map((r) => [r[keyField], r]));

  // Merged key list preserving order
  const allKeys = [
    ...new Set([
      ...(rowsA ?? []).map((r) => r[keyField]),
      ...(rowsB ?? []).map((r) => r[keyField]),
    ]),
  ];

  const rows = [];

  // Header row
  if (isSingle) {
    rows.push([keyLabel, ...valueCols.map((c) => c.label)]);
  } else {
    // Interleave A / B columns per metric, then a Winner column
    const headers = [keyLabel];
    valueCols.forEach(({ label }) => {
      headers.push(`${nameA} — ${label}`);
      headers.push(`${nameB} — ${label}`);
    });
    headers.push("Winner");
    rows.push(headers);
  }

  if (allKeys.length === 0) {
    rows.push(["No data for selected filters"]);
    return makeSheet(rows);
  }

  // Data rows
  allKeys.forEach((key) => {
    const ra = mapA[key] ?? {};
    const rb = mapB[key] ?? {};

    if (isSingle) {
      rows.push([key, ...valueCols.map(({ field }) => Number(ra[field] ?? 0))]);
    } else {
      const dataRow = [key];
      let aWins = 0;
      let bWins = 0;

      valueCols.forEach(({ field }) => {
        const va = Number(ra[field] ?? 0);
        const vb = Number(rb[field] ?? 0);
        dataRow.push(va);
        dataRow.push(vb);
        if (va > vb) aWins++;
        else if (vb > va) bWins++;
      });

      let winnerLabel = "Tie";
      if (aWins > bWins) winnerLabel = nameA;
      else if (bWins > aWins) winnerLabel = nameB;
      dataRow.push(winnerLabel);

      rows.push(dataRow);
    }
  });

  // TOTAL row with Excel SUM formulas
  // Data starts at row 2 (1-indexed), header is row 1
  const dataStartRow = 2;
  const dataEndRow = 1 + allKeys.length;
  const totalRow = ["TOTAL"];
  const numValueCols = isSingle ? valueCols.length : valueCols.length * 2;

  for (let ci = 0; ci < numValueCols; ci++) {
    // Column index: B = 1, C = 2, ... (0-based offset from col B)
    const colLetter = XLSX.utils.encode_col(ci + 1); // +1 to skip key column
    totalRow.push({
      f: `SUM(${colLetter}${dataStartRow}:${colLetter}${dataEndRow})`,
      t: "n",
    });
  }

  // In compare mode there's a Winner column at the end — leave it blank in totals
  if (!isSingle) totalRow.push("");

  rows.push(totalRow);

  return makeSheet(rows);
};

// ─────────────────────────────────────────────────────────────────────────────
// Main export function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * exportSchemeStats
 *
 * @param {object} statsA   - raw stats response for scheme A
 * @param {object|null} statsB  - raw stats response for scheme B (null = single mode)
 * @param {string} nameA    - scheme A display name
 * @param {string} nameB    - scheme B display name
 * @param {object|null} winner  - result from computeWinner() or null
 */
export const exportSchemeStats = (
  statsA,
  statsB,
  nameA,
  nameB,
  winner = null,
) => {
  const sA = getFilteredStats(statsA);
  const sB = statsB ? getFilteredStats(statsB) : null;

  if (!sA) {
    console.warn("exportSchemeStats: no statsA data");
    return;
  }

  const wb = XLSX.utils.book_new();

  // ── Sheet 1: Overview ──────────────────────────────────────────────────────
  addSheet(wb, buildOverviewSheet(sA, sB, nameA, nameB, winner), "Overview");

  // ── Sheet 2: State-wise ────────────────────────────────────────────────────
  addSheet(
    wb,
    buildDetailSheet({
      rowsA: sA.states,
      rowsB: sB?.states ?? null,
      keyField: "state",
      keyLabel: "State",
      valueCols: [
        { field: "total_participants", label: "Participants" },
        { field: "total_points_earned", label: "Points Earned" },
        { field: "total_points_redeemed", label: "Points Redeemed" },
      ],
      nameA,
      nameB,
    }),
    "State-wise",
  );

  // ── Sheet 3: Product Codes ─────────────────────────────────────────────────
  addSheet(
    wb,
    buildDetailSheet({
      rowsA: sA.product_codes,
      rowsB: sB?.product_codes ?? null,
      keyField: "product_code",
      keyLabel: "Product Code",
      valueCols: [
        { field: "total_scans", label: "Total Scans" },
        { field: "unique_users", label: "Unique Users" },
        { field: "total_points_earned", label: "Points Earned" },
      ],
      nameA,
      nameB,
    }),
    "Product Codes",
  );

  // ── Sheet 4: Categories ────────────────────────────────────────────────────
  addSheet(
    wb,
    buildDetailSheet({
      rowsA: sA.product_categories,
      rowsB: sB?.product_categories ?? null,
      keyField: "category_name",
      keyLabel: "Category",
      valueCols: [
        { field: "total_scans", label: "Total Scans" },
        { field: "unique_users", label: "Unique Users" },
        { field: "total_points_earned", label: "Points Earned" },
      ],
      nameA,
      nameB,
    }),
    "Categories",
  );

  // ── Sheet 5: Points Range ──────────────────────────────────────────────────
  addSheet(
    wb,
    buildDetailSheet({
      rowsA: sA.points_range_distribution,
      rowsB: sB?.points_range_distribution ?? null,
      keyField: "range",
      keyLabel: "Points Range",
      valueCols: [{ field: "count", label: "Users" }],
      nameA,
      nameB,
    }),
    "Points Range",
  );

  // ── Trigger download ───────────────────────────────────────────────────────
  const isSingle = !sB;
  const fileName = isSingle
    ? `${nameA.replace(/\s+/g, "_")}_stats.xlsx`
    : `${nameA.replace(/\s+/g, "_")}_vs_${nameB.replace(/\s+/g, "_")}_stats.xlsx`;

  XLSX.writeFile(wb, fileName);
};
